import { ApiError } from "@/common/lib/api";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useLotsList } from "@/features/lots/api/lotsApi";
import { useEffect, useState } from "react";
import { useChangeAnimalLot } from "../api/animalsApi";

type Props = {
  accountId: string;
  animalId: string;
  currentLotId: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
};

export function ChangeLotDialog({
  accountId,
  animalId,
  currentLotId,
  open,
  onOpenChange,
  onSuccess,
}: Props) {
  const lotsQuery = useLotsList(accountId);
  const mutation = useChangeAnimalLot(accountId, animalId);

  const [toLotId, setToLotId] = useState<string>("");
  const [reason, setReason] = useState("");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setToLotId("");
    setReason("");
    setError(null);
  }, [open]);

  const allLots = lotsQuery.data?.lots ?? [];
  // Solo lotes activos como destino, y excluir el actual.
  const availableLots = allLots.filter((l) => l.isActive && l.id !== currentLotId);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);

    if (!currentLotId) {
      setError("El animal no está asignado a ningún lote.");
      return;
    }
    if (!toLotId) {
      setError("Selecciona un lote destino.");
      return;
    }

    try {
      await mutation.mutateAsync({
        fromLotId: currentLotId,
        toLotId,
        reason: reason.trim() || undefined,
      });
      onOpenChange(false);
      onSuccess?.();
    } catch (e) {
      if (e instanceof ApiError) {
        setError(e.message);
      } else {
        setError("Ocurrió un error inesperado.");
      }
    }
  }

  const pending = mutation.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Cambiar de lote</DialogTitle>
          <DialogDescription>
            El animal saldrá del lote actual y entrará al lote destino con la fecha de hoy.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          <div className="flex flex-col gap-1.5">
            <label htmlFor="to-lot" className="font-medium text-[13px] text-app-ink-2">
              Lote destino
            </label>
            <Select value={toLotId} onValueChange={setToLotId} disabled={pending}>
              <SelectTrigger
                id="to-lot"
                className="h-10 w-full text-app-ink-1"
                aria-label="Lote destino"
              >
                <SelectValue placeholder="Selecciona un lote" />
              </SelectTrigger>
              <SelectContent>
                {availableLots.length === 0 ? (
                  <div className="px-3 py-2 text-[13px] text-app-ink-3">
                    No hay otros lotes activos disponibles.
                  </div>
                ) : (
                  availableLots.map((lot) => (
                    <SelectItem key={lot.id} value={lot.id}>
                      {lot.name}
                    </SelectItem>
                  ))
                )}
              </SelectContent>
            </Select>
          </div>

          <div className="flex flex-col gap-1.5">
            <label htmlFor="lot-reason" className="font-medium text-[13px] text-app-ink-2">
              Razón <span className="font-normal text-app-ink-3">(opcional)</span>
            </label>
            <textarea
              id="lot-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              maxLength={500}
              rows={3}
              disabled={pending}
              className="w-full rounded-lg border border-app-border bg-app-surface px-3 py-2 text-app-ink-1 text-base outline-none focus-visible:border-app-emerald focus-visible:ring-3 focus-visible:ring-app-emerald/20"
              placeholder="Ej. Movimiento por preñez confirmada"
            />
          </div>

          {error && (
            <p
              role="alert"
              className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-[13px] text-red-700"
            >
              {error}
            </p>
          )}

          <DialogFooter>
            <DialogClose asChild>
              <button
                type="button"
                disabled={pending}
                className="inline-flex h-10 items-center justify-center rounded-xl border border-app-border bg-app-surface px-4 font-medium text-[14px] text-app-ink-1 transition-colors hover:bg-app-surface-2 disabled:opacity-50"
              >
                Cancelar
              </button>
            </DialogClose>
            <button
              type="submit"
              disabled={pending || availableLots.length === 0}
              className="inline-flex h-10 items-center justify-center rounded-xl bg-app-ink-1 px-4 font-medium text-[14px] text-white transition-colors hover:bg-[#1e211d] disabled:opacity-50"
            >
              {pending ? "Cambiando…" : "Cambiar"}
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
